import { useState, useEffect } from "react"
import {
  Search,
  Filter,
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import type { SalesReport } from "@/features/agents"
import { AgentService } from "@/features/agents"

interface FilterState {
  status?: string
  startDate?: string
  endDate?: string
  campaignId?: string
  searchTerm?: string
}

interface PaginationState {
  currentPage: number
  itemsPerPage: number
  totalItems: number
  totalPages: number
}

export default function AgentSalesReports() {
  const [reports, setReports] = useState<SalesReport[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<FilterState>({})
  const [showFilters, setShowFilters] = useState(false)
  const [selectedReport, setSelectedReport] = useState<SalesReport | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0,
  })

  // Mock data for demonstration - expanded dataset
  const mockReports: SalesReport[] = []

  useEffect(() => {
    loadReports()
  }, [pagination.currentPage, pagination.itemsPerPage])

  const loadReports = async () => {
    setLoading(true)
    try {
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status
      }
      
      const reportsData = await AgentService.getSalesReports(apiFilters)
      setReports(reportsData)

      // Update pagination info
      const filteredData = getFilteredReports(reportsData)
      setPagination((prev) => ({
        ...prev,
        totalItems: filteredData.length,
        totalPages: Math.ceil(filteredData.length / prev.itemsPerPage),
      }))
    } catch (error) {
      console.error("Failed to load sales reports:", error)
      // Fallback to empty array on error
      setReports([])
      setPagination((prev) => ({
        ...prev,
        totalItems: 0,
        totalPages: 0,
      }))
    } finally {
      setLoading(false)
    }
  }

  const getFilteredReports = (allReports: SalesReport[]) => {
    return allReports.filter((report) => {
      if (filters.status && report.status !== filters.status) return false
      if (filters.startDate && report.reportDate < filters.startDate) return false
      if (filters.endDate && report.reportDate > filters.endDate) return false
      if (filters.campaignId && !report.campaignId.toLowerCase().includes(filters.campaignId.toLowerCase()))
        return false
      if (filters.searchTerm && 
          !report.legalGuardianFullName.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
          !report.videoGamerFullName.toLowerCase().includes(filters.searchTerm.toLowerCase()))
        return false
      return true
    })
  }

  const filteredReports = getFilteredReports(reports)

  // Get paginated data
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage
  const endIndex = startIndex + pagination.itemsPerPage
  const paginatedReports = filteredReports.slice(startIndex, endIndex)

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case "REJECTED":
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />
    }
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full"
    switch (status) {
      case "APPROVED":
        return `${baseClasses} bg-green-100 text-green-800`
      case "REJECTED":
        return `${baseClasses} bg-red-100 text-red-800`
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`
    }
  }

  const exportReports = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Legal Guardian,Video Gamer,Campaign,Status,Report Date,Created Date\n" +
      filteredReports
        .map(
          (r) =>
            `"${r.legalGuardianFullName}","${r.videoGamerFullName}","${r.campaignName}","${r.status}","${r.reportDate}","${new Date(r.createdAt).toLocaleDateString()}"`,
        )
        .join("\n")

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `sales_reports_${new Date().toISOString().split("T")[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const viewDetails = (report: SalesReport) => {
    setSelectedReport(report)
    setShowDetails(true)
  }

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }))
  }

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage,
      currentPage: 1,
      totalPages: Math.ceil(filteredReports.length / itemsPerPage),
    }))
  }

  const applyFilters = () => {
    const filteredData = getFilteredReports(reports)
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      totalItems: filteredData.length,
      totalPages: Math.ceil(filteredData.length / prev.itemsPerPage),
    }))
  }

  // Calculate summary statistics
  const totalSales = filteredReports.length
  const approvedSales = filteredReports.filter((r) => r.status === "APPROVED").length
  const pendingCount = filteredReports.filter((r) => r.status === "PENDING").length
  const approvedCount = filteredReports.filter((r) => r.status === "APPROVED").length
  // const rejectedCount = filteredReports.filter((r) => r.status === "REJECTED").length

  // Pagination component
  const PaginationControls = () => {
    const { currentPage, totalPages } = pagination
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-accent-200 sm:px-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-accent-700">Show</span>
            <select
              value={pagination.itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-accent-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-accent-700">per page</span>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-accent-700">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredReports.length)} of {filteredReports.length}{" "}
              results
            </span>
          </div>

          <div className="flex items-center space-x-1">
            {/* First page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            {/* Previous page */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page numbers */}
            {pages.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 text-sm rounded ${
                  page === currentPage ? "bg-primary-600 text-white" : "text-accent-700 hover:bg-accent-100"
                }`}
              >
                {page}
              </button>
            ))}

            {/* Next page */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last page */}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">My Sales Reports</h2>
          <p className="text-accent-600">Track your sales performance and commission reports</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={exportReports} className="btn-outline flex items-center justify-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Total Sales</p>
              <p className="text-2xl font-bold text-accent-900">{totalSales}</p>
            </div>
            <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Approved Sales</p>
              <p className="text-2xl font-bold text-green-600">{approvedSales}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Success Rate</p>
              <p className="text-2xl font-bold text-primary-600">
                {filteredReports.length > 0 ? Math.round((approvedCount / filteredReports.length) * 100) : 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <h3 className="text-lg font-semibold text-accent-900 mb-4">Filter Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Status</label>
              <select
                className="input-field"
                value={filters.status || ""}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Campaign</label>
              <input
                type="text"
                className="input-field"
                placeholder="Campaign ID"
                value={filters.campaignId || ""}
                onChange={(e) => setFilters({ ...filters, campaignId: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Start Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.startDate || ""}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">End Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.endDate || ""}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button onClick={() => setFilters({})} className="btn-outline">
              Clear Filters
            </button>
            <button onClick={applyFilters} className="btn-primary">
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by client name..."
            className="input-field pl-10"
            value={filters.searchTerm || ""}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              // Auto-apply search filter
              setTimeout(applyFilters, 300)
            }}
          />
        </div>
      </div>

      {/* Reports Table */}
      <div className="card p-0">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-accent-600">Loading reports...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-accent-200">
                <thead className="bg-accent-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Client Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Report Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-accent-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.id} className="hover:bg-accent-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <FileText className="w-4 h-4 text-primary-600" />
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            <div>{report.legalGuardianFullName}</div>
                            <div className="text-xs text-accent-600">{report.videoGamerFullName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-accent-900">{report.campaignName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(report.status)}
                          <span className={getStatusBadge(report.status)}>{report.status}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-accent-500">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(report.reportDate).toLocaleDateString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => viewDetails(report)}
                          className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {paginatedReports.length === 0 && !loading && (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-accent-300 mx-auto mb-4" />
                  <p className="text-accent-500">No sales reports found matching your criteria.</p>
                  <p className="text-sm text-accent-400 mt-1">Try adjusting your filters or search terms.</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {filteredReports.length > 0 && <PaginationControls />}
          </>
        )}
      </div>

      {/* Report Details Modal */}
      {showDetails && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-accent-900">Sales Report Details</h3>
              <button className="text-accent-500 hover:text-accent-700" onClick={() => setShowDetails(false)}>
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-accent-600">Report ID</label>
                    <p className="text-accent-900 font-mono">{selectedReport.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Legal Guardian</label>
                    <p className="text-accent-900">{selectedReport.legalGuardianFullName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Video Gamer</label>
                    <p className="text-accent-900">{selectedReport.videoGamerFullName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Campaign</label>
                    <p className="text-accent-900">{selectedReport.campaignName}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-accent-600">Status</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusIcon(selectedReport.status)}
                      <span className={getStatusBadge(selectedReport.status)}>{selectedReport.status}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Report Date</label>
                    <p className="text-accent-900">{new Date(selectedReport.reportDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Created Date</label>
                    <p className="text-accent-900">{new Date(selectedReport.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Last Updated</label>
                    <p className="text-accent-900">{new Date(selectedReport.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* {selectedReport.status === "REJECTED" && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Rejection Reason</h4>
                  <p className="text-sm text-red-700">
                    This report was rejected due to incomplete documentation. Please ensure all required fields are
                    properly filled and supporting documents are attached.
                  </p>
                </div>
              )} */}

              {selectedReport.status === "PENDING" && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Pending Review</h4>
                  <p className="text-sm text-yellow-700">
                    This report is currently under review by the HR team. You will be notified once the review is
                    complete.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              <button className="btn-outline" onClick={() => setShowDetails(false)}>
                Close
              </button>
              <button className="btn-primary">Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
